/**
 * IF Account Card — one row per owned Instant-Funding account.
 *
 * Live-state fields are zeroed in v1 (DO snapshots aren't piped into D1
 * yet); the card surfaces "No Live Data" alongside the static rule
 * derivations. Once live data lands, the same card lights up.
 */

import { type ReactNode } from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import type { IfAccountSummary } from "@/lib/api/accounts-matcher";
import { CoolIcon } from "@/components/ui/cool-icon";
import { FirmAvatar } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import { cn } from "@/lib/utils";

export function IfAccountCard({
  account,
  index,
}: {
  account: IfAccountSummary;
  index: number;
}): ReactNode {
  const logo = firmLogoUrl(account.firmId) ?? account.firmLogo ?? "";
  const slMax = account.derived.perTradeSlMaxUsd;
  const lifetimeBudget = account.derived.lifetimeSlHitBudget;
  const liveDataMissing = account.risk.totalProfitUsd === 0 && account.risk.biggestWinningDayUsd === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1],
        delay: Math.min(index, 8) * 0.04,
      }}
      className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <FirmAvatar src={logo} name={account.firmName} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {account.firmName}
            </p>
            <p className="truncate font-mono text-[11px] text-muted-foreground">
              {account.accountLabel ?? account.login ?? account.slug}
            </p>
          </div>
        </div>
        {account.pairedWithSlug ? (
          <Link
            to={`/pairs/${account.accountPairSlug}`}
            className="flex items-center gap-1 rounded-md border border-accent/30 bg-accent/10 px-2 py-1 text-[10px] uppercase tracking-wide text-accent hover:bg-accent/20"
          >
            <CoolIcon name="Link_Horizontal" size={11} />
            Paired
          </Link>
        ) : (
          <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
            Orphan
          </span>
        )}
      </div>

      <dl className="grid grid-cols-3 gap-2 border-y border-border/40 py-3 text-[11px]">
        <Stat label="Size" value={`$${account.risk.startingBalance.toLocaleString()}`} />
        <Stat
          label="Per-Trade SL"
          value={slMax != null ? `$${formatUsd(slMax)}` : "—"}
          accent
        />
        <Stat label="Lifetime Hits" value={`${lifetimeBudget}`} />
      </dl>

      <ul className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground">
        <RuleRow label="Floating" value={pctOrNone(account.risk.maxFloatingPct)} />
        <RuleRow label="Daily DD" value={pct(account.risk.dailyDdPct)} />
        <RuleRow label="Lifetime DD" value={pct(account.risk.lifetimeDdPct)} />
        <RuleRow label="Consistency" value={pctOrNone(account.risk.consistencyPct)} />
      </ul>

      {liveDataMissing && (
        <div className="flex items-center gap-1.5 rounded-md border border-dashed border-border/60 bg-muted/20 px-2 py-1.5 text-[10px] text-muted-foreground">
          <CoolIcon name="Info" size={11} />
          <span>No Live Equity Yet — Showing Static Caps Only</span>
        </div>
      )}
    </motion.div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "font-mono text-sm font-semibold tabular-nums",
          accent ? "text-accent" : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function RuleRow({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <li className="flex items-center justify-between border-b border-border/30 py-1 last:border-b-0">
      <span>{label}</span>
      <span className="font-mono text-foreground/80">{value}</span>
    </li>
  );
}

function pct(v: number): string {
  return `${(v * 100).toFixed(v < 0.01 ? 2 : 1).replace(/\.?0+$/, "")}%`;
}

function pctOrNone(v: number | null): string {
  return v == null ? "None" : pct(v);
}

function formatUsd(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
